'use client';
import { FC, useState } from 'react';
import Link from 'next/link';
import { Prism, SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { coldarkDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { Logo } from '~/components/common/logo';
import { API_BASE_URL } from '~/config';
import { cn } from '~/lib/utils';
import { BlazingFastInferenceSkeleton, ProvenReliabilitySkeleton, TunedForUseCaseSkeleton } from './skeletons';
import { Heading } from '~/components/ui/Heading';

// TODO: Remove this when @type/react-syntax-highlighter is updated
const SyntaxHighlighter = Prism as unknown as FC<SyntaxHighlighterProps>;
export const BentoGrid = () => {
  return (
    <div className="px-4 py-20 sm:px-2 md:py-40">
      <Heading className="my-20 text-center">
        The{' '}
        <span className="from-primary-400 to-primary-100 dark:from-primary-700 dark:to-primary-500 text-shadow-md  inline-block bg-gradient-to-r bg-clip-text italic text-transparent">
          fastest
        </span>{' '}
        and most uncompromising AI platform
      </Heading>

      <div className="grid grid-cols-1 grid-rows-2 gap-2 md:grid-cols-6">
        <div className="col-span-1 rounded-2xl bg-gray-100 p-2 md:col-span-3 md:p-10 dark:bg-zinc-800">
          <BlazingFastInferenceSkeleton />
          <p className="text-lg font-bold text-neutral-900 dark:text-neutral-200">Industry Leading Performance</p>
          <p className="mt-2 text-sm font-normal text-neutral-600 dark:text-neutral-400">
            Independently benchmarked to have the top speed of all inference providers
          </p>
        </div>
        <div className="col-span-1 rounded-2xl bg-gray-100 p-2 md:col-span-3 md:p-10 dark:bg-zinc-800">
          <ProvenReliabilitySkeleton />
          <p className="text-lg font-bold text-neutral-900 dark:text-neutral-200">Enterprise Scale Throughput</p>
          <div className="mt-2 text-sm font-normal text-neutral-600 dark:text-neutral-400">
            Our proprietary stack blows open source options out of the water{' '}
            <Link
              href="https://fireworks.ai/blog/fire-attention-serving-open-source-models-4x-faster-than-vllm-by-quantizing-with-no-tradeoffs"
              target="__blank"
              className="text-primary-400"
            >
              (see blog)
            </Link>
          </div>
        </div>
        <div className="col-span-1 flex h-full flex-col  justify-between rounded-2xl bg-gray-100 p-2 md:col-span-2 md:p-10 dark:bg-zinc-800">
          <Link
            href="https://fireworks.ai/blog/firellava-the-first-commercially-permissive-oss-llava-model"
            target="__blank"
            className="bg-primary-400 dark:bg-primary-200 relative flex h-60 w-full flex-col items-center justify-center overflow-hidden rounded-lg p-4 transition duration-200 hover:scale-105"
          >
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-50 h-20 bg-gray-100 [mask-image:linear-gradient(to_bottom,transparent,white)] dark:bg-zinc-800" />
            <div className="bg-grid-small-slate-200/20 pointer-events-none absolute inset-0" />

            <Logo />
            <p className="relative z-20 mt-4 text-center font-bold text-white">
              FireLLaVA: the first commercially permissive OSS LLaVA model
            </p>
          </Link>
          <div className="">
            <p className="text-base font-bold text-neutral-900 dark:text-neutral-200">State-of-the-art Models</p>
            <p className="mt-2 text-sm font-normal text-neutral-600 dark:text-neutral-400">
              Use powerful models curated by Fireworks or our in-house trained multi-modal and function-calling models
            </p>
          </div>
        </div>
        <div className="col-span-1 flex h-full flex-col  justify-between rounded-2xl bg-gray-100 p-2 md:col-span-2 md:p-10 dark:bg-zinc-800">
          <TunedForUseCaseSkeleton />
          <div className="">
            <p className="text-base font-bold text-neutral-900 dark:text-neutral-200">Battle Tested for Reliability</p>
            <div className="mt-2 text-sm font-normal text-neutral-600 dark:text-neutral-400">
              Fireworks is the{' '}
              <Link
                href="https://blog.langchain.dev/langchain-state-of-ai-2023/"
                className="inline rounded-md bg-gray-200 px-0.5 py-0.5 dark:bg-zinc-700"
              >
                2nd most used open-source model provider
              </Link>{' '}
              and also generates over 1M images/day
              <br />
              <br />
            </div>
          </div>
        </div>
        <div className="col-span-1 flex h-full flex-col  justify-between rounded-2xl bg-gray-100 p-2 md:col-span-2 md:p-10 dark:bg-zinc-800">
          <APICode />
          <div>
            <p className="text-base font-bold text-neutral-900 dark:text-neutral-200">Built for Developers</p>
            <p className="mt-2 text-sm font-normal text-neutral-600 dark:text-neutral-400">
              Our OpenAI-compatible API makes it easy to start building with Fireworks!
              <br />
              <br />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const APICode = () => {
  const ts = `fetch("${API_BASE_URL}/inference/v1/chat/completions", {
  method: "POST",
  headers: {
    "Content-Type": "application
    "Authorization: "Bearer <API KEY>",
  },
  body: JSON.stringify({
    model: "accounts/fireworks/mixtral-8x7b",
    prompt: "Say this is a test",
    max_tokens: 700,
  }),
})
  `;

  const python = `import fireworks.client

fireworks.client.configure(
  api_base="${API_BASE_URL}/inference",
  api_key="<API_KEY>",
)

fireworks.client.Completion.create(
    model="accounts/fireworks/mixtral-8x7b",
    prompt="Say this is a test",
    max_tokens=7,
    temperature=0,
)
  `;

  const [activeTab, setActiveTab] = useState<'ts' | 'python'>('ts');
  const [code, setCode] = useState(ts);

  const tabs = ['ts', 'python'];

  return (
    <div className="relative h-60 w-full  overflow-hidden rounded-lg bg-zinc-900 dark:bg-zinc-700">
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-50 h-40 bg-zinc-900 [mask-image:linear-gradient(to_bottom,transparent,white)] dark:bg-zinc-800" />
      <div className="flex items-center justify-start space-x-1 p-2">
        <div className="h-2 w-2 rounded-full bg-red-500" />
        <div className="h-2 w-2 rounded-full bg-gray-500" />
        <div className="h-2 w-2 rounded-full bg-green-500" />
      </div>
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => {
            setActiveTab(tab as any);
            setCode(tab === 'ts' ? ts : python);
          }}
          className={cn(
            'w-20 border-b border-transparent px-4 py-2 text-sm text-neutral-400',
            activeTab === tab && 'text-primary-500 border-primary-500 ',
          )}
        >
          {tab === 'ts' ? 'TS' : 'Python'}
        </button>
      ))}
      <SyntaxHighlighter
        language={activeTab}
        style={coldarkDark}
        PreTag="div"
        showLineNumbers={false}
        customStyle={{
          width: '100%',
          background: 'transparent',
          padding: '1rem 1rem',
          overflow: 'hidden',
        }}
        lineNumberContainerStyle={{
          display: 'none',
        }}
        codeTagProps={{
          style: {
            fontSize: '0.8rem',
            fontFamily: 'var(--font-mono)',
          },
        }}
      >
        {code}
      </SyntaxHighlighter>
      {/* <pre className="overflow-y-auto !bg-transparent overflow-x-auto font-mono !m-0 text-[10px] ">
        <code className="language-python"></code>
      </pre> */}
    </div>
  );
};
